import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASEURL_POLYMER, SUB_PATH_BASIC } from "../../utils/api";
import { SolicitorCase } from "../../models/classes/solicitor/SolicitorCase.class";
import { CooSubmission } from "../../models/classes/CooSubmission.class";
import { SolicitorCaseSummary } from "../../models/classes/solicitor/SolicitorCaseSummary.class";
import { SolicitorFinancialDetailsResponse } from "../../models/classes/solicitor/SolicitorFinancialDetails.class";
import { SolicitorCorrespondenceResponse } from "../../models/classes/solicitor/SolicitorCorrespondenceResponse.class";
import { FileContent, FileItem } from "../../models/polymer/Document.model";
import { SolicitorCompletionDetail } from "../../models/classes/solicitor/SolicitorCompletionDetail.class";
import { GroupSubmittedDocument, SolicitorSubmission } from "../../models/classes/solicitor/SolicitorSubmission.class";
import { SolicitorNotification } from "../../models/classes/solicitor/SolicitorNotification.class";
import { StageDMessageInfo, StageDocumentsInfo } from "../../models/classes/stage-d/Review-Application.class";
import { PolymerCrypto } from "../../utils/encryptor";
import { Helpers } from "../../utils/helper";
import { Pageable } from "../../models/interfaces/pageable.interface";
import { SolicitorNotificationTagEnum } from "../../models/enum/solicitor-notification-tag.enum";

interface QueryParams {
  uinfin: string;
  singpass_token: string;
}

interface WithAppUuid {
  application_uuid: string;
}

interface WithUen {
  uen: string;
}

interface WithSearchTermAndPagination {
  tag: SolicitorNotificationTagEnum | null;
  search_term?: string;
  page: number;
  size?: number;
}

export const solicitorCaseApi = createApi({
  reducerPath: "solicitorCaseApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BASEURL_POLYMER,
    prepareHeaders: (headers, { getState }) => {
      headers.set("Access-Control-Request-Method", "POST");
      headers.set("X-Content-Type-Options", "nosniff");
    },
  }),
  endpoints: (builder) => ({
    getSolicitorCase: builder.query<Pageable<SolicitorCase>, QueryParams & WithUen & WithSearchTermAndPagination>({
      query: (queryParams: QueryParams & WithUen & WithSearchTermAndPagination) => {
        const { uinfin, uen, singpass_token, tag, search_term = "", page, size = 10 } = queryParams;

        const url: string = tag
          ? `${SUB_PATH_BASIC}/solicitor/cases?tag=${tag}&search_term=${encodeURIComponent(search_term)}&page=${page}&size=${size}`
          : `${SUB_PATH_BASIC}/solicitor/cases?search_term=${encodeURIComponent(search_term)}&page=${page}&size=${size}`;

        return {
          method: "GET",
          url,
          headers: {
            uinfin,
            uen,
            Authorization: `Bearer ${singpass_token}`,
          },
        };
      },
      keepUnusedDataFor: 10,
    }),
    getSolicitorCaseDetail: builder.query<CooSubmission, QueryParams & WithAppUuid & WithUen>({
      query: (queryParams: QueryParams & WithAppUuid & WithUen) => ({
        method: "GET",
        url: `${SUB_PATH_BASIC}/solicitor/applicationDetail?applicationUuid=${queryParams.application_uuid}`,
        headers: {
          uinfin: queryParams.uinfin,
          uen: queryParams.uen,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 10,
    }),
    getSolicitorNotification: builder.query<SolicitorNotification, QueryParams & WithAppUuid & WithUen>({
      query: (queryParams: QueryParams & WithAppUuid & WithUen) => ({
        method: "GET",
        url: `${SUB_PATH_BASIC}/solicitor/notification?applicationUuid=${queryParams.application_uuid}`,
        headers: {
          uinfin: queryParams.uinfin,
          uen: queryParams.uen,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 10,
    }),
    getSolicitorCaseSummary: builder.query<SolicitorCaseSummary, QueryParams & WithAppUuid & WithUen>({
      query: (queryParams: QueryParams & WithAppUuid & WithUen) => ({
        method: "GET",
        url: `${SUB_PATH_BASIC}/solicitor/applicationSummary?applicationUuid=${queryParams.application_uuid}`,
        headers: {
          uinfin: queryParams.uinfin,
          uen: queryParams.uen,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 10,
    }),
    getSolicitorSubmissions: builder.query<SolicitorSubmission, QueryParams & WithAppUuid & WithUen>({
      query: (queryParams: QueryParams & WithAppUuid & WithUen) => ({
        method: "GET",
        url: `${SUB_PATH_BASIC}/solicitor/submissions?applicationUuid=${queryParams.application_uuid}`,
        headers: {
          uinfin: queryParams.uinfin,
          uen: queryParams.uen,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 10,
      transformResponse: (response: SolicitorSubmission, meta: any) => {
        let submittedDocuments = response?.additional_documents ?? [];
        const groupedSubmissions: GroupSubmittedDocument[] = [];
        if (submittedDocuments && submittedDocuments?.length > 0) {
          submittedDocuments.sort((a, b) => (a.received_date ?? "").localeCompare(b.received_date ?? ""));
          for (const document of submittedDocuments) {
            const mappedDocument = new FileItem({
              uinfin: document.uinfin ? PolymerCrypto.decryption(document.uinfin) : "",
              file_id: document.uuid,
              key: document.key,
              code: document.code,
              label: document.label,
              file_content: new FileContent({
                id: Helpers.generateAlphaNumericID(),
                file_name: document.file_name,
                document_type: document.document_type,
                size: document.file_size ?? 0,
              }),
            });
            const findGroupSubmission = groupedSubmissions.find((item) => item.title === document.label && item.submitted_date_time === document.received_date);
            if (!findGroupSubmission) {
              groupedSubmissions.push({
                title: document.label ?? "",
                submitted_date_time: document.received_date ?? "",
                submitted_by: document.submitted_by ? PolymerCrypto.decryption(document.submitted_by) : "",
                documents: [mappedDocument],
                additional_info: {},
              });
            } else {
              findGroupSubmission.documents = [...findGroupSubmission.documents, mappedDocument];
            }
          }
        }
        return {
          message_info: !response.message_info ? [] : response.message_info.map((message) => new StageDMessageInfo(message)),
          folders: !response.folders ? [] : response.folders.map((folder) => new StageDocumentsInfo(folder)),
          additional_documents: submittedDocuments, //raw response for document submissions, will not be used for now
          grouped_additional_documents: groupedSubmissions.map((item) => new GroupSubmittedDocument(item)), //grouped response for document submissions, will be used for submission history modal
        };
      },
    }),
    getSolicitorFinancialDetails: builder.query<SolicitorFinancialDetailsResponse, QueryParams & WithAppUuid & WithUen>({
      query: (queryParams: QueryParams & WithAppUuid & WithUen) => ({
        method: "GET",
        url: `${SUB_PATH_BASIC}/solicitor/financialDetails?applicationUuid=${queryParams.application_uuid}`,
        headers: {
          uinfin: queryParams.uinfin,
          uen: queryParams.uen,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 10,
    }),
    getSolicitorCorrespondenceDetails: builder.query<SolicitorCorrespondenceResponse, QueryParams & WithAppUuid & WithUen>({
      query: (queryParams: QueryParams & WithAppUuid & WithUen) => ({
        method: "GET",
        url: `${SUB_PATH_BASIC}/solicitor/correspondenceDetails?applicationUuid=${queryParams.application_uuid}`,
        headers: {
          uinfin: queryParams.uinfin,
          uen: queryParams.uen,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 10,
    }),
    getSolicitorCompletionDetails: builder.query<SolicitorCompletionDetail, QueryParams & WithAppUuid & WithUen>({
      query: (queryParams: QueryParams & WithAppUuid & WithUen) => ({
        method: "GET",
        url: `${SUB_PATH_BASIC}/solicitor/completionDetails?applicationUuid=${queryParams.application_uuid}`,
        headers: {
          uinfin: queryParams.uinfin,
          uen: queryParams.uen,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 10,
      transformResponse: (query: SolicitorCompletionDetail, meta: any) => {
        return new SolicitorCompletionDetail(query);
      },
    }),
  }),
});

export const {
  useGetSolicitorCaseQuery,
  useGetSolicitorCaseDetailQuery,
  useGetSolicitorNotificationQuery,
  useGetSolicitorCaseSummaryQuery,
  useGetSolicitorSubmissionsQuery,
  useGetSolicitorFinancialDetailsQuery,
  useGetSolicitorCorrespondenceDetailsQuery,
  useGetSolicitorCompletionDetailsQuery,
} = solicitorCaseApi;
